import React, {memo, useEffect, useRef, useState} from "react";
import "../index.scss";
import PaperPlaneRightLight from "assets/mcat-chat/paper-plane-right-light.svg";
import PaperPlaneRightDark from "assets/mcat-chat/paper-plane-right-dark.svg";
import {Typography} from "../../../atoms";
import {ValidateErrorIcon} from "../../../atoms/Icon";
import scrollIntoView from 'scroll-into-view-if-needed';
import {useDeviceDetect} from "../../../../hooks";
import {useBrowserUIDetection} from "../../../../hooks";
import { AI_CHAT_SEND_MESSAGE } from "utils/constants/GoogleTagGUIDs";

interface ChatDrawerFooterProps {
  theme: string;
  onSend: (message: string) => void;
  placeholder?: string;
  isLoadingPreviousChat: boolean;
}

export const ChatDrawerFooter: React.FC<ChatDrawerFooterProps> = memo(({ theme, onSend, isLoadingPreviousChat, placeholder = "Send a message...",}) => {

  const device = useDeviceDetect();
  const {bottomOffset, requiresManualPadding} = useBrowserUIDetection(device);

  const [message, setMessage] = useState("");
  const [textDirection, setTextDirection] = useState('auto');

  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // to determine the text direction whether RTL OR LTR
  useEffect(() => {
    const hasRTL = /[\u0591-\u07FF\uFB1D-\uFDFD\uFE70-\uFEFC]/.test(message);
    setTextDirection(hasRTL ? 'rtl' : 'ltr');
  }, [message]);

  // sometimes in some devices text input covered to keyboard. this fix use to make it work like ios devices
  function scrollInputIfCovered(el: HTMLElement, visualViewPort: VisualViewport) {
    const rect = el.getBoundingClientRect();
    const viewportHeight = visualViewPort.height;
    const buffer = visualViewPort.height * 0.15;

    const isCoveredByKeyboard = rect.bottom > viewportHeight - buffer;

    if (isCoveredByKeyboard) {
      const scrollAmount = rect.bottom - (viewportHeight - buffer);
      window.scrollBy({
        top: scrollAmount,
        behavior: 'smooth',
      });
    } else {
      scrollIntoView(el, {
        scrollMode: 'if-needed',
        block: 'nearest',
        behavior: 'smooth',
      });
    }
  }

  // this will call the outer function for process the message
  const handleSend = (e?: any) => {
    if (message.trim() && !isLoadingPreviousChat && message?.length <= 2000) {
      if (e) e.stopPropagation();
      onSend(message);
      setTextDirection('auto');
      setMessage("");

      // reset height after send
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
    }
  };

  // this will triggered when typing
  const onChange = (e: any) => {
    setMessage(e.target.value);
    autoResize();
  }

  // when focusing the input, keyboard doesn't cover. using scrollIntoView library make it work
  const onFocus = () => {
    if (textareaRef.current) {
      textareaRef.current.focus();
      setTimeout(() => {
        if (window.visualViewport && textareaRef.current) {
          scrollInputIfCovered(textareaRef.current, window.visualViewport);
        }
      }, 300);
    }
  };

  // use to handle the enter behaviour. enter to send. but not with shift
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // text input resize behaviour is handling here
  const autoResize = () => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      const maxHeight = 24 * 10;
      textarea.style.height = Math.min(textarea.scrollHeight, maxHeight) + 'px';
    }
  };

  return (
      <div
          className="chat-drawer-footer"
          style={{
            marginBottom: requiresManualPadding ? `${bottomOffset}px` : 'env(safe-area-inset-bottom, 5px)'
          }}
      >
        <div
            onClick={onFocus}
            className="message-input-container"
        >
          <textarea
              dir={textDirection}
              className="message-input"
              placeholder={placeholder}
              onFocus={onFocus}
              value={message}
              onChange={onChange}
              onKeyDown={handleKeyPress}
              rows={1}
              ref={textareaRef}
              style={{textAlign: textDirection === 'rtl' ? 'right' : 'left',}}
          />
          <button
              className="send-button"
              onClick={handleSend}
              disabled={!message.trim()}
              data-tracking-id={AI_CHAT_SEND_MESSAGE}
          >
          <span className="send-icon">
            {
              theme === "dark" ?
                  <img src={PaperPlaneRightDark} alt="send-icon-arrow" />
                  :
                  <img src={PaperPlaneRightLight} alt="send-icon-arrow" />
            }
          </span>
          </button>
        </div>

        <div className={`character-limit-text ${message?.length > 2000 ? 'red-text' : '' }`}>
          {
            message?.length > 2000 &&
              <ValidateErrorIcon color={'red'}/>
          }
          <Typography
              variant="h9Book"
              label={message?.length ? `${message?.length > 2000 ? 'Character limit exceeded ' : ''}${message.length}/2000` : '\u00A0'}
          />
        </div>

      </div>
  );
})
